import Cookies from 'js-cookie';
import { getValue } from '../../modules/crm/inPatient/service/crmUtils';
import { ALLOWED_FILE_TYPES } from '../constants/commonConstants';
import {decryptText} from '../../utils/encryptDecrypt';

class ApplicationDetail {

  private static _instance: ApplicationDetail = new ApplicationDetail();
  private static userDetail: any;
  private static hospitalDetail: any;
  private static userAccess: any;
  private static groupProviders: any;
  private static isMasterDataUpdateAllowed = false;

  constructor() {
    if (ApplicationDetail._instance) {
      throw new Error('Use ApplicationDetail.instance');
    }
    ApplicationDetail._instance = this;
  }

  static get instance(): ApplicationDetail {
    return ApplicationDetail._instance;
  }

  private setUserDetail(userDetail: any): void {
    ApplicationDetail.userDetail = userDetail;
  }

  private setHospitalDetail(hospitalDetail: any): void {
    ApplicationDetail.hospitalDetail = hospitalDetail;
  }

  private setUserAccess(userAccessObject: any): void {
    ApplicationDetail.userAccess = userAccessObject;
  }

  private setGroupProviders(groupProviders: any = []): void {
    ApplicationDetail.groupProviders = groupProviders;
  }

  setApplicationDetail(appDetail: any): any {
    this.setUserDetail(appDetail.userDetails);
    this.setHospitalDetail(appDetail.providerDetail);
    this.setUserAccess(appDetail.featureList);
    this.setGroupProviders(appDetail.groupProviders);
  }

  setIsMasterDataUpdateAllowed(isAllowed: boolean): void {
    ApplicationDetail.isMasterDataUpdateAllowed = isAllowed;
  }

  setIsNotificationsEnabled(isNotificationsEnabled: boolean): void {
    ApplicationDetail.hospitalDetail.providerConfigurableProperties.isNotificationEnabled = isNotificationsEnabled;
  }

  getIsMasterDataUpdateAllowed(): boolean {
    return ApplicationDetail.isMasterDataUpdateAllowed;
  }

  getUserDetail(): any {
    return ApplicationDetail.userDetail;
  }

  getHospitalDetail(): any {
    return ApplicationDetail.hospitalDetail;
  }

  getGroupProviders(): any {
    return ApplicationDetail.groupProviders;
  }

  getConfiguredPayer(): any {
    return ApplicationDetail?.hospitalDetail?.configuredPayers || [];
  }

  getMaTpaId(): number {
    return 1063871;
  }

  getMaTpaName(): string {
    return 'Medi Assist Insurance TPA Pvt. Ltd.';
  }

  getConfiguredSchemes(): any[] {
    return ApplicationDetail?.hospitalDetail?.configuredSchemes || [];
  }

  getUserName(): string {
    return ApplicationDetail?.userDetail?.userName;
  }

  getAccessToken(): string {
    return Cookies.get('r2_dto') || '';
  }

  getRohiniCode(): string {
    return ApplicationDetail?.hospitalDetail?.providerRohiniCode;
  }

  getProviderCity(): any {
    return ApplicationDetail.hospitalDetail.providerCity;
  }
  getProviderId(): string {
    return ApplicationDetail?.hospitalDetail?.providerId;
  }
  getProviderName(): string {
    return ApplicationDetail?.hospitalDetail?.providerName;
  }
  getProfileId(): number {
    if (ApplicationDetail?.userDetail) {
      return ApplicationDetail?.userDetail?.profileId;
    } else {
      const userDetails = JSON.parse(decryptText(localStorage.getItem('userDetail') || '{}') as any);
      return userDetails?.profileId;
    }
  }
  getUserRole(): any {
    const userDetails = JSON.parse(decryptText(localStorage.getItem('userDetail') || '{}') as any);
    const { userRoles } = userDetails;
    return userRoles;
  }
  getFeatureList(): any {
    return ApplicationDetail.userAccess;
  }
  getIfAccessEnabled(accessCheckKeyword: string): boolean {
    return !!getValue(ApplicationDetail.userAccess, `[\'${accessCheckKeyword}\']`);
  }
  getIsNewAutomationEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isNewAutomationEnabled\']');
  }
  getisHospitalNumberEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isHospitalNumberEnabled\']');
  }
  getIsBetaHospital(): boolean {
    return (
      process.env.REACT_APP_CURRENT_ENVIRONMENT === 'production' &&
      (getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isBetaHospital\']'))
    );
  }

  getIsTPADashboardEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isTPADashboardEnabled\']');
  }

  getIsSurveyEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isSurveyEnabled\']');
  }

  getIsEmailVerificationEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isEmailVerificationEnabled\']');
  }

  getIsNotificationEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isNotificationEnabled\']');
  }

  getHipId(): string {
    return getValue(ApplicationDetail.hospitalDetail, '[\'Hip_ID\']');
  }

  getAllowedFileExtension(): any {
    const newAllowedFileTypes: any =
      ApplicationDetail?.userDetail?.allowedFileTypes
        ? {...ApplicationDetail?.userDetail?.allowedFileTypes} : {...ALLOWED_FILE_TYPES};

    const convertedLabels = newAllowedFileTypes.LABEL.toString().replace(/','/g, ', ');
    newAllowedFileTypes.LABEL =
      convertedLabels.substring(0, convertedLabels.lastIndexOf(',') + 1) +
      ' or' +
      convertedLabels.substring(convertedLabels.lastIndexOf(',') + 1 );

    return newAllowedFileTypes;
  }

  getIsPOIEnabledForHospital(): any {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isPOIEnabled\']');
  }
  getIsEasyDischargeEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isEasyDischargeEnabled\']');
  }
  getIsHISSearchEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isHISIntegrationEnabled\']');
  }
  getIsNHCXEnabledProvider(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isNHCXEnabledProvider\']');
  }
  getIsDocumentChecklistEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isDocumentChecklistEnabled\']');
  }
  getIsDownloadClaimEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isDownloadClaimEnabled\']');
  }
  getIsNewMailboxConfigEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isNewMailboxConfigEnabled\']');
  }
  getTickerConfigurator(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'tickerConfigurations\']');
  }
  getIsPreAuthNewDesignEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isPreAuthNewDesignEnabled\']');
  }
  getIsInitialDischargeEnabledProvider(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isInitialDischargeEnabledProvider\']');
  }
  getIsUserEmailVerified(): string {
    return ApplicationDetail?.userDetail?.isEmailVerified;
  }
  getIsVersioningPostAPIEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isVersioningPostAPIEnabled\']');
  }
  getIsNewTpaDeskEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isNewTpaDeskEnabled\']');
  }

  getIsGrievanceEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isGrievanceEnabled\']');
  }

  getIsHospitalSealFeatureEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isHospitalSealFeatureEnabled\']');
  }

  getIsDoctorSignatureFeatureEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isDoctorSignatureEnabled\']');
  }
  getIsPatientSignEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isPatientSignatureEnabled\']');
  }
  getProviderSpecificFeatures(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'providerSpecificFeatures\']');
  }
  getIsContractedHospital(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isContractedHospital\']');
  }

  getIsOrionIntegrationEnabled(): boolean {
    return getValue(ApplicationDetail.hospitalDetail, '[\'providerConfigurableProperties\'][\'isOrionEnabled\']');
  }
}

export default ApplicationDetail;
